/**
 * S-1006: Modal Manager - Centralized modal state management
 * Prevents race conditions, manages stacking, handles focus, and cleans up zombie backdrops
 */

export type ModalType = 'payments' | 'feedback' | 'alert' | 'confirmation' | 'custom';

export interface ModalConfig {
  id: string;
  type: ModalType;
  priority: number; // Higher priority modals appear on top
  canStack: boolean; // Whether this modal can be stacked with others
  blockingLevel: 'none' | 'same-type' | 'all'; // What other modals this blocks
  escapeKeyHandler?: () => void;
  onClose?: () => void;
  metadata?: Record<string, any>;
}

export interface ModalState {
  isOpen: boolean;
  config: ModalConfig;
  openedAt: number;
  zIndex: number;
}

export type ModalEventType = 'opened' | 'closed' | 'blocked' | 'stacked' | 'focus-changed';

export interface ModalEvent {
  type: ModalEventType;
  modalId: string;
  timestamp: number;
  metadata?: Record<string, any>;
}

/**
 * Centralized modal manager to prevent race conditions and manage modal stacking
 */
export class ModalManager {
  private static instance: ModalManager | null = null;
  private modals: Map<string, ModalState> = new Map();
  private listeners: Set<(event: ModalEvent) => void> = new Set();
  private baseZIndex = 1000;
  private cleanupTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private focusStack: string[] = []; // Track focus order for ESC key handling

  private constructor() {
    this.setupGlobalEventHandlers();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): ModalManager {
    if (!ModalManager.instance) {
      ModalManager.instance = new ModalManager();
    }
    return ModalManager.instance;
  }

  /**
   * AC1: Register a modal with the manager
   * S-2212: Enhanced modal registration with deterministic cleanup
   */
  public registerModal(config: ModalConfig): boolean {
    // S-2212: If registering the same modal that's already open, close it first
    if (this.modals.has(config.id)) {
      console.log(`[ModalManager] Re-registering modal ${config.id}, cleaning up previous instance`);
      this.unregisterModal(config.id, true);
    }

    // Check if modal can be opened based on blocking rules
    if (!this.canOpenModal(config)) {
      this.emitEvent({
        type: 'blocked',
        modalId: config.id,
        timestamp: Date.now(),
        metadata: { reason: 'blocked by existing modal' }
      });
      return false;
    }

    // S-2212: For non-stackable modals, ensure clean swap by closing others first
    if (!config.canStack) {
      const openModals = this.getOpenModals();
      openModals.forEach(existingModal => {
        if (existingModal.id !== config.id) {
          console.log(`[ModalManager] Closing ${existingModal.id} for non-stackable modal ${config.id}`);
          this.unregisterModal(existingModal.id, true);
        }
      });
    }

    // Calculate z-index based on priority and existing modals
    const zIndex = this.calculateZIndex(config);

    const modalState: ModalState = {
      isOpen: true,
      config,
      openedAt: Date.now(),
      zIndex
    };

    this.modals.set(config.id, modalState);
    this.focusStack.push(config.id);

    // Clear any existing cleanup timeout for this modal
    this.clearCleanupTimeout(config.id);

    this.emitEvent({
      type: 'opened',
      modalId: config.id,
      timestamp: Date.now(),
      metadata: { zIndex, priority: config.priority }
    });

    console.log(`[ModalManager] Registered modal: ${config.id} (z-index: ${zIndex})`);
    return true;
  }

  /**
   * AC2: Unregister a modal and handle cleanup
   */
  public unregisterModal(modalId: string, immediate = false): void {
    const modalState = this.modals.get(modalId);
    if (!modalState) {
      console.warn(`[ModalManager] Attempted to unregister unknown modal: ${modalId}`);
      return;
    }

    // Remove from focus stack
    this.focusStack = this.focusStack.filter(id => id !== modalId);

    if (immediate) {
      this.performModalCleanup(modalId);
    } else {
      // AC2: Schedule cleanup to prevent zombie backdrops
      this.scheduleCleanup(modalId);
    }

    this.emitEvent({
      type: 'closed',
      modalId,
      timestamp: Date.now(),
      metadata: { immediate }
    });

    console.log(`[ModalManager] Unregistered modal: ${modalId} (immediate: ${immediate})`);
  }

  /**
   * AC3: Handle ESC key for topmost modal
   */
  public handleEscapeKey(): boolean {
    if (this.focusStack.length === 0) {
      return false;
    }

    // Get the topmost modal (last in focus stack)
    const topModalId = this.focusStack[this.focusStack.length - 1];
    const modalState = this.modals.get(topModalId);

    if (!modalState || !modalState.isOpen) {
      // Clean up stale focus stack entry
      this.focusStack = this.focusStack.filter(id => id !== topModalId);
      return this.handleEscapeKey(); // Try next modal
    }

    // Call the modal's escape handler if it exists
    if (modalState.config.escapeKeyHandler) {
      modalState.config.escapeKeyHandler();
      return true;
    }

    // Default behavior: close the modal
    if (modalState.config.onClose) {
      modalState.config.onClose();
      return true;
    }

    return false;
  }

  /**
   * AC4: Get current modal stack for focus management
   */
  public getModalStack(): ModalConfig[] {
    return this.focusStack
      .map(id => this.modals.get(id))
      .filter((state): state is ModalState => state !== undefined && state.isOpen)
      .sort((a, b) => a.zIndex - b.zIndex)
      .map(state => state.config);
  }

  /**
   * Get the topmost modal
   */
  public getTopModal(): ModalConfig | null {
    const stack = this.getModalStack();
    return stack.length > 0 ? stack[stack.length - 1] : null;
  }

  /**
   * Check if a specific modal is open
   */
  public isModalOpen(modalId: string): boolean {
    const state = this.modals.get(modalId);
    return state?.isOpen ?? false;
  }

  /**
   * Get all open modals
   */
  public getOpenModals(): ModalConfig[] {
    return Array.from(this.modals.values())
      .filter(state => state.isOpen)
      .sort((a, b) => a.zIndex - b.zIndex)
      .map(state => state.config);
  }

  /**
   * Force close all modals (emergency cleanup)
   */
  public closeAllModals(): void {
    const openModals = Array.from(this.modals.keys());
    openModals.forEach(modalId => {
      this.unregisterModal(modalId, true);
    });
    this.focusStack = [];
    console.log('[ModalManager] Force closed all modals');
  }

  /**
   * Add event listener
   */
  public addEventListener(listener: (event: ModalEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Check if a modal can be opened based on blocking rules
   */
  private canOpenModal(config: ModalConfig): boolean {
    const openModals = this.getOpenModals();

    for (const existingModal of openModals) {
      // Check if existing modal blocks this one
      switch (existingModal.blockingLevel) {
        case 'all':
          return false;
        case 'same-type':
          if (existingModal.type === config.type) {
            return false;
          }
          break;
        case 'none':
          break;
      }

      // Check if new modal blocks existing ones
      switch (config.blockingLevel) {
        case 'all':
          return openModals.length === 0;
        case 'same-type':
          if (openModals.some(modal => modal.type === config.type)) {
            return false;
          }
          break;
        case 'none':
          break;
      }

      // Check stacking rules
      if (!config.canStack || !existingModal.canStack) {
        return false;
      }
    }

    return true;
  }

  /**
   * Calculate z-index for a modal based on priority and existing modals
   */
  private calculateZIndex(config: ModalConfig): number {
    const openModals = this.getOpenModals();
    
    if (openModals.length === 0) {
      return this.baseZIndex;
    }

    // Find the highest z-index
    const maxZIndex = Math.max(...openModals.map(modal => {
      const state = this.modals.get(modal.id);
      return state?.zIndex ?? this.baseZIndex;
    }));

    // Add based on priority
    return maxZIndex + 10 + config.priority;
  }

  /**
   * Schedule cleanup to prevent zombie backdrops
   */
  private scheduleCleanup(modalId: string): void {
    // Clear any existing timeout
    this.clearCleanupTimeout(modalId);

    // Schedule cleanup after animation completes
    const timeout = setTimeout(() => {
      this.performModalCleanup(modalId);
    }, 300); // Match typical modal animation duration

    this.cleanupTimeouts.set(modalId, timeout);
  }

  /**
   * Perform actual modal cleanup
   */
  private performModalCleanup(modalId: string): void {
    this.modals.delete(modalId);
    this.clearCleanupTimeout(modalId);

    // Clean up any zombie backdrops
    this.cleanupZombieBackdrops();
  }

  /**
   * Clear cleanup timeout for a modal
   */
  private clearCleanupTimeout(modalId: string): void {
    const timeout = this.cleanupTimeouts.get(modalId);
    if (timeout) {
      clearTimeout(timeout);
      this.cleanupTimeouts.delete(modalId);
    }
  }

  /**
   * AC2: Clean up zombie backdrops that may be left behind
   * S-2212: Improved deterministic cleanup to prevent zombie backdrops
   */
  private cleanupZombieBackdrops(): void {
    // S-2212: Only run cleanup if we have no active modals to prevent interference
    if (this.modals.size > 0) {
      return;
    }

    // S-2212: More precise detection of truly orphaned overlays
    const dialogOverlays = document.querySelectorAll('[data-radix-dialog-overlay]');
    const alertOverlays = document.querySelectorAll('[data-radix-alert-dialog-overlay]');
    
    // Combine all overlay selectors
    const allOverlays = [...dialogOverlays, ...alertOverlays];
    
    let cleanedCount = 0;
    
    allOverlays.forEach(overlay => {
      const element = overlay as HTMLElement;
      
      // S-2212: Enhanced orphan detection - must be closed AND have no active modal parent
      const isOrphaned = (
        element.getAttribute('data-state') === 'closed' &&
        // Check if element is inside an open modal (with fallback for test environments)
        (typeof element.closest === 'function' ? !element.closest('[data-state="open"]') : true) &&
        (element.style.display === 'none' || 
         element.style.opacity === '0' ||
         (typeof element.hasAttribute === 'function' && element.hasAttribute('data-exiting'))) // Radix UI exit animation
      );
      
      if (isOrphaned) {
        // S-2212: Only log in development and only if we actually clean something
        try {
          element.remove();
          cleanedCount++;
        } catch (error) {
          // S-2212: Silent failure for production stability
          if (import.meta.env.DEV) {
            console.warn('[ModalManager] Failed to remove orphaned overlay:', error);
          }
        }
      }
    });

    // S-2212: Clean up empty portals more conservatively
    const portals = document.querySelectorAll('[data-radix-portal]');
    portals.forEach(portal => {
      const element = portal as HTMLElement;
      // Only remove if completely empty and not actively used
      if (!element.hasChildNodes() && 
          !(typeof element.hasAttribute === 'function' && element.hasAttribute('data-radix-dialog-portal')) &&
          !(typeof element.hasAttribute === 'function' && element.hasAttribute('data-radix-alert-dialog-portal'))) {
        try {
          element.remove();
          cleanedCount++;
        } catch (error) {
          // Silent failure
        }
      }
    });

    // S-2212: Only log if we actually cleaned something and in dev mode
    if (cleanedCount > 0 && import.meta.env.DEV) {
      console.log(`[ModalManager] Cleaned ${cleanedCount} orphaned elements`);
    }
  }

  /**
   * Setup global event handlers
   */
  private setupGlobalEventHandlers(): void {
    // AC3: Global ESC key handler
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        const handled = this.handleEscapeKey();
        if (handled) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    });

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
      this.closeAllModals();
    });

    // S-2212: Conservative periodic cleanup - only when no modals are active
    setInterval(() => {
      if (this.modals.size === 0) {
        // Only run cleanup if there are potential orphaned elements
        const hasOrphanedElements = document.querySelectorAll(
          '[data-radix-dialog-overlay][data-state="closed"], [data-radix-alert-dialog-overlay][data-state="closed"]'
        ).length > 0;
        
        if (hasOrphanedElements) {
          this.cleanupZombieBackdrops();
        }
      }
    }, 15000); // S-2212: Further reduced frequency to 15s for less interference
  }

  /**
   * Emit event to listeners
   */
  private emitEvent(event: ModalEvent): void {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('[ModalManager] Event listener error:', error);
      }
    });
  }
}

// Export singleton instance
export const modalManager = ModalManager.getInstance();

// Export convenience functions
export const registerModal = (config: ModalConfig) => modalManager.registerModal(config);
export const unregisterModal = (modalId: string, immediate?: boolean) => modalManager.unregisterModal(modalId, immediate);
export const isModalOpen = (modalId: string) => modalManager.isModalOpen(modalId);
export const getModalStack = () => modalManager.getModalStack();
export const closeAllModals = () => modalManager.closeAllModals();
